import { EProcessingStatus } from '@src/types/automationDocument'
import { DocumentPage } from '@src/types/documentPage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface StagedPagesDiff {
  deletedIds: string[]
  typeChangesByTypeId: Map<number, string[]>
  rotations: { id: string; rotation: number }[]
  reorders: { id: string; pageNumber: number }[]
}

interface StackEntry {
  pages: DocumentPage[]
  affectedPageIds: string[]
}

export interface StagedStepResult {
  affectedPageIds: string[]
  restoredPages: DocumentPage[]
}

export interface UseStagedPagesResult {
  current: DocumentPage[]
  baseline: DocumentPage[]
  isDirty: boolean
  canUndo: boolean
  canRedo: boolean
  changeTypes: (pageIds: string[], documentTypeId: number) => void
  deletePages: (pageIds: string[]) => void
  rotatePage: (pageId: string, degrees: number) => void
  reorder: (filteredPagesNewOrder: DocumentPage[], filteredPageIds: Set<string>) => void
  undo: () => StagedStepResult
  redo: () => StagedStepResult
  undoAll: () => void
  computeDiff: () => StagedPagesDiff
  acceptServerPages: (pages: DocumentPage[]) => void
}

const EMPTY_STEP: StagedStepResult = { affectedPageIds: [], restoredPages: [] }

const pagesEqual = (a: DocumentPage[], b: DocumentPage[]): boolean => {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const pa = a[i]
    const pb = b[i]
    if (
      pa.id !== pb.id ||
      pa.documentTypeId !== pb.documentTypeId ||
      pa.processingStatus !== pb.processingStatus ||
      pa.rotation !== pb.rotation ||
      pa.pageNumber !== pb.pageNumber
    ) {
      return false
    }
  }
  return true
}

export const useStagedPages = (initialPages: DocumentPage[]): UseStagedPagesResult => {
  const [baseline, setBaseline] = useState<DocumentPage[]>(initialPages)
  const [current, setCurrent] = useState<DocumentPage[]>(initialPages)
  const [undoStack, setUndoStack] = useState<StackEntry[]>([])
  const [redoStack, setRedoStack] = useState<StackEntry[]>([])

  const currentRef = useRef(current)
  useEffect(() => {
    currentRef.current = current
  }, [current])

  const isDirty = useMemo(() => undoStack.length > 0 || !pagesEqual(current, baseline), [undoStack, current, baseline])

  const stage = useCallback(
    (affectedPageIds: string[], producer: (prev: DocumentPage[]) => DocumentPage[]) => {
      setUndoStack((stack) => [...stack, { pages: currentRef.current, affectedPageIds }])
      setRedoStack([])
      setCurrent((prev) => producer(prev))
    },
    [],
  )

  const changeTypes = useCallback(
    (pageIds: string[], documentTypeId: number) => {
      if (pageIds.length === 0) return
      const ids = new Set(pageIds)
      stage(pageIds, (prev) => prev.map((p) => (ids.has(p.id) ? { ...p, documentTypeId } : p)))
    },
    [stage],
  )

  const deletePages = useCallback(
    (pageIds: string[]) => {
      if (pageIds.length === 0) return
      const ids = new Set(pageIds)
      stage(pageIds, (prev) =>
        prev.map((p) => (ids.has(p.id) ? { ...p, processingStatus: EProcessingStatus.Discarded } : p)),
      )
    },
    [stage],
  )

  const rotatePage = useCallback(
    (pageId: string, degrees: number) => {
      stage([pageId], (prev) =>
        prev.map((p) => {
          if (p.id !== pageId) return p
          const next = ((p.rotation || 0) + degrees) % 360
          const normalized = next < 0 ? next + 360 : next
          return { ...p, rotation: normalized }
        }),
      )
    },
    [stage],
  )

  const reorder = useCallback(
    (filteredPagesNewOrder: DocumentPage[], filteredPageIds: Set<string>) => {
      const affected = filteredPagesNewOrder.map((p) => p.id)
      stage(affected, (prev) => {
        const result: DocumentPage[] = []
        let filteredIdx = 0
        let pageNumber = 1
        for (const p of prev) {
          if (filteredPageIds.has(p.id)) {
            const reordered = filteredPagesNewOrder[filteredIdx++]
            result.push({ ...reordered, pageNumber: pageNumber++ })
          } else {
            result.push({ ...p, pageNumber: pageNumber++ })
          }
        }
        return result
      })
    },
    [stage],
  )

  const undo = useCallback((): StagedStepResult => {
    const stack = undoStack
    if (stack.length === 0) return EMPTY_STEP
    const entry = stack[stack.length - 1]
    setRedoStack((r) => [...r, { pages: currentRef.current, affectedPageIds: entry.affectedPageIds }])
    setUndoStack(stack.slice(0, -1))
    setCurrent(entry.pages)
    return { affectedPageIds: entry.affectedPageIds, restoredPages: entry.pages }
  }, [undoStack])

  const redo = useCallback((): StagedStepResult => {
    const stack = redoStack
    if (stack.length === 0) return EMPTY_STEP
    const entry = stack[stack.length - 1]
    setUndoStack((u) => [...u, { pages: currentRef.current, affectedPageIds: entry.affectedPageIds }])
    setRedoStack(stack.slice(0, -1))
    setCurrent(entry.pages)
    return { affectedPageIds: entry.affectedPageIds, restoredPages: entry.pages }
  }, [redoStack])

  const undoAll = useCallback(() => {
    setUndoStack([])
    setRedoStack([])
    setCurrent(baseline)
  }, [baseline])

  const computeDiff = useCallback((): StagedPagesDiff => {
    const baselineById = new Map(baseline.map((p) => [p.id, p]))

    const deletedIds: string[] = []
    const typeChangesByTypeId = new Map<number, string[]>()
    const rotations: { id: string; rotation: number }[] = []
    const reorders: { id: string; pageNumber: number }[] = []

    let anyReorder = false

    for (const p of current) {
      const base = baselineById.get(p.id)
      if (!base) continue

      const becameDiscarded =
        p.processingStatus === EProcessingStatus.Discarded && base.processingStatus !== EProcessingStatus.Discarded
      if (becameDiscarded) {
        deletedIds.push(p.id)
        continue
      }

      if (p.processingStatus === EProcessingStatus.Discarded) continue

      if (p.documentTypeId !== base.documentTypeId && p.documentTypeId != null) {
        const typeId = Number(p.documentTypeId)
        if (!Number.isNaN(typeId)) {
          const ids = typeChangesByTypeId.get(typeId) ?? []
          ids.push(p.id)
          typeChangesByTypeId.set(typeId, ids)
        }
      }

      if ((p.rotation ?? 0) !== (base.rotation ?? 0)) {
        rotations.push({ id: p.id, rotation: p.rotation ?? 0 })
      }

      if (p.pageNumber !== base.pageNumber) {
        anyReorder = true
      }
    }

    if (anyReorder) {
      for (const p of current) {
        if (p.processingStatus === EProcessingStatus.Discarded) continue
        if (p.pageNumber != null) {
          reorders.push({ id: p.id, pageNumber: p.pageNumber })
        }
      }
    }

    return { deletedIds, typeChangesByTypeId, rotations, reorders }
  }, [current, baseline])

  const acceptServerPages = useCallback((pages: DocumentPage[]) => {
    setBaseline(pages)
    setCurrent(pages)
    setUndoStack([])
    setRedoStack([])
  }, [])

  return {
    current,
    baseline,
    isDirty,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    changeTypes,
    deletePages,
    rotatePage,
    reorder,
    undo,
    redo,
    undoAll,
    computeDiff,
    acceptServerPages,
  }
}
